"use client";

import { Coins, Users, TrendingUp } from "lucide-react";
import { useLeadModalStore } from "@/stores/useLeadModalStore";
import Link from "next/link";
import { useT } from "@/hooks/useT";

export default function RewardsSection() {
  const t = useT();
  const { open } = useLeadModalStore();

  const rewards = [
    {
      key: "frenpoints",
      name: t("pricing.frenpoints.name"),
      icon: <Coins className="w-7 h-7" />,
      subtitle: t("pricing.frenpoints.subtitle"),
      features: [
        { text: t("pricing.frenpoints.f1"), highlight: "0.1 FP" },
        { text: t("pricing.frenpoints.f2"), highlight: "0.5 FP" },
        { text: t("pricing.frenpoints.f3"), highlight: "1–100 FP" },
        { text: t("pricing.frenpoints.f4"), highlight: "streaks" },
      ],
      highlight: false,
      cta: t("pricing.frenpoints.cta"),
      ctaAction: open,
    },
    {
      key: "referral",
      name: t("pricing.referral.name"),
      icon: <Users className="w-7 h-7" />,
      subtitle: t("pricing.referral.subtitle"),
      features: [
        { text: t("pricing.referral.f1"), highlight: "10%" },
        { text: t("pricing.referral.f2"), highlight: "+10 FP" },
        { text: t("pricing.referral.f3"), highlight: "Real-time" },
        { text: t("pricing.referral.f4"), highlight: "Share" },
      ],
      highlight: true,
      badge: t("pricing.referral.badge"),
      cta: t("pricing.referral.cta"),
      ctaHref: "/affiliate",
    },
    {
      key: "aura",
      name: t("pricing.aura.name"),
      icon: <TrendingUp className="w-7 h-7" />,
      subtitle: t("pricing.aura.subtitle"),
      features: [
        { text: t("pricing.aura.f1"), highlight: "1.25x" },
        { text: t("pricing.aura.f2"), highlight: "badges" },
        { text: t("pricing.aura.f3"), highlight: "400 FP/day" },
        { text: t("pricing.aura.f4"), highlight: "Aura" },
      ],
      highlight: false,
      cta: t("pricing.aura.cta"),
      ctaAction: open,
    },
  ];

  return (
    <section
      id="points"
      className="bg-white dark:bg-[#0f0f12] py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-16"
    >
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8 sm:mb-12 text-center">
        <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          {t("pricing.eyebrow")}
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-4">
          {t("pricing.headline.before")}{" "}
          <span className="text-accent-primary">
            {t("pricing.headline.accent")}
          </span>
        </h2>
        <p className="text-lg font-satoshi text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          {t("pricing.subtitle")}
        </p>
      </div>

      {/* Reward Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-8 mx-auto md:grid-cols-3 max-w-7xl">
        {rewards.map((reward) => {
          const isHighlight = reward.highlight;
          return (
            <div
              key={reward.key}
              className={`relative bg-white dark:bg-[#18181D] rounded-[5px] p-5 sm:p-8 flex flex-col justify-between border ${
                isHighlight ? "border-gray-900 dark:border-white/20" : "border-gray-200 dark:border-[#1A1A1A]"
              }`}
            >
              {reward.badge && (
                <div className="absolute top-0 px-3 py-1 -mt-3 text-xs font-inter-tight font-bold -translate-x-1/2 rounded-[5px] left-1/2 bg-gray-900 dark:bg-white text-white dark:text-gray-900">
                  {reward.badge}
                </div>
              )}

              <div className="text-3xl mb-4 text-center text-gray-400 dark:text-gray-500 flex justify-center">
                {reward.icon}
              </div>
              <h3 className="text-xl font-monument font-bold text-gray-900 dark:text-white mb-1 text-center">{reward.name}</h3>
              <p className="text-gray-500 dark:text-gray-400 font-satoshi text-sm mb-6 italic text-center">
                {reward.subtitle}
              </p>

              <ul className="text-sm font-satoshi text-gray-600 dark:text-gray-400 space-y-3 mb-6 flex-grow">
                {reward.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0">&#10003;</span>
                    <span>
                      {f.text.split(f.highlight).map((part, i, arr) => (
                        <span key={i}>
                          {part}
                          {i < arr.length - 1 && (
                            <span className="font-bold text-gray-900 dark:text-white">{f.highlight}</span>
                          )}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>

              {reward.ctaHref ? (
                <Link
                  href={reward.ctaHref}
                  className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-semibold px-6 py-2 rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors text-center"
                >
                  {reward.cta}
                </Link>
              ) : (
                <button
                  onClick={reward.ctaAction}
                  className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-semibold px-6 py-2 rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  {reward.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Aura Levels + OG Pass Teaser */}
      <div className="mt-8 sm:mt-12 max-w-3xl mx-auto text-center space-y-3">
        <div className="bg-gray-50 dark:bg-[#18181D] rounded-[5px] p-6 border border-gray-200 dark:border-[#1A1A1A]">
          <p className="text-sm font-satoshi text-gray-600 dark:text-gray-400 mb-2">
            <span className="font-bold text-gray-900 dark:text-white">{t("pricing.auraTiers.label")}</span>{" "}
            {t("pricing.auraTiers.list")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {t("pricing.auraTiers.decay")}
          </p>
        </div>

        {/* OG FREN Pass teaser hidden temporarily (May 2026) — page at /og-pass
            still exists. Restore when the genesis pass campaign re-opens.
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 font-satoshi">
          <Crown className="w-4 h-4" />
          <span>
            <Link href="/og-pass" className="font-bold text-gray-900 dark:text-white hover:underline">OG FREN Pass</Link>
            {" "}— {t("pricing.ogPass.suffix")}
          </span>
        </div>
        */}
      </div>
    </section>
  );
}
